import { useState, useMemo } from 'react'
import { Search, ChevronDown, ChevronRight, Layers } from 'lucide-react'
import { useOpsBridgeFeatures } from '@/hooks/useApi'

type OpsCap = {
  platform_feature_id: string
  name: string
  description: string | null
  category: string
  tool_count: number
  tool_names: string
  tier_summary: string | null
  location: string | null
  source_doc: string
  last_generated: string | null
  status: string
}

function CapRow({ cap }: { cap: OpsCap }) {
  const [open, setOpen] = useState(false)
  let tools: string[] = []
  try { tools = JSON.parse(cap.tool_names) } catch { tools = cap.tool_names ? cap.tool_names.split(',').map(t => t.trim()) : [] }
  return (
    <div className="border-b border-gray-200 dark:border-white/5 last:border-0">
      <button
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-white/[0.03] text-left transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        {open
          ? <ChevronDown className="w-3 h-3 text-gray-400 dark:text-white/20 shrink-0" />
          : <ChevronRight className="w-3 h-3 text-gray-400 dark:text-white/20 shrink-0" />}
        <span className="flex-1 text-[11px] font-mono text-gray-800 dark:text-white/80 truncate min-w-0">{cap.name}</span>
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0 text-blue-600 dark:text-blue-400 bg-blue-600/10 dark:bg-blue-400/10">
          {cap.tool_count} tool{cap.tool_count === 1 ? '' : 's'}
        </span>
      </button>
      {open && (
        <div className="px-9 pb-3 pt-1 space-y-1.5 bg-gray-50 dark:bg-white/[0.01]">
          {cap.description && (
            <p className="text-[11px] text-gray-600 dark:text-white/50 leading-relaxed">{cap.description}</p>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] text-gray-500 dark:text-white/40 font-mono">
            {cap.location && <span>worker: <span className="text-gray-700 dark:text-white/60">{cap.location}</span></span>}
            {cap.last_generated && (
              <span>catalogued: <span className="text-gray-700 dark:text-white/60">{new Date(cap.last_generated).toLocaleDateString()}</span></span>
            )}
          </div>
          {tools.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {tools.map(t => (
                <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-white/50">{t}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function OpsBridgeFeaturesPanel() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const { data, isLoading, error } = useOpsBridgeFeatures()

  const caps = useMemo(() => {
    const list = (data?.platform_features as OpsCap[]) || []
    return list.filter(c => {
      if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !(c.description || '').toLowerCase().includes(search.toLowerCase())) return false
      if (category !== 'all' && c.category !== category) return false
      return true
    })
  }, [data, search, category])

  const categories = useMemo(() => Object.keys(data?.summary?.by_category || {}), [data])

  return (
    <div className="relative flex flex-col h-full rounded-2xl border overflow-hidden bg-white/75 border-white/60 shadow-[0_4px_24px_rgba(0,0,0,0.07),0_1px_4px_rgba(0,0,0,0.04)] backdrop-blur-md dark:bg-black/[0.28] dark:border-white/[0.10] dark:shadow-[0_4px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.06)] dark:backdrop-blur-md">
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-gray-500 dark:text-white/40" />
            <h2 className="text-xs font-bold text-gray-700 dark:text-white/70 uppercase tracking-widest">OpsBridge Features</h2>
          </div>
          {data && (
            <span className="text-[10px] font-mono text-gray-400 dark:text-white/30">{caps.length}/{data.total} groups &middot; {data.total_tools} tools</span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-32">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-white/20" />
            <input
              type="text"
              placeholder="Search features or descriptions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-6 pr-2 py-1 text-[11px] font-mono bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded text-gray-800 dark:text-white/70 placeholder:text-gray-400 dark:placeholder:text-white/20 focus:outline-none focus:border-gray-400 dark:focus:border-white/20"
            />
          </div>
          {categories.length > 0 && (
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="glass text-[11px] font-mono rounded px-2 py-1 text-gray-700 dark:text-white/60 focus:outline-none"
            >
              <option value="all">All categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-white/30 font-mono">Loading OpsBridge features…</div>
        )}
        {error && (
          <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-white/30 font-mono">OpsBridge features pending backend deployment</div>
        )}
        {!isLoading && !error && caps.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-white/30 font-mono">No features match the current filters</div>
        )}
        {caps.map(cap => (
          <CapRow key={cap.platform_feature_id} cap={cap} />
        ))}
      </div>
    </div>
  )
}
